import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { MoreHorizontal } from "lucide-react";
import markUrl from "@/icons/nelgates-mark.png";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { UserMenu } from "./UserMenu";
import { MoreMenu } from "./MoreMenu";
import { CustomizeNav } from "./CustomizeNav";
import { usePinnedNav } from "./usePinnedNav";
import type { NavItem } from "./nav";

type Nav = ReturnType<typeof import("./nav").useNav>;

const badgeText = (n: number) => (n > 99 ? "99+" : String(n));

/** The More menu lists categories side by side only where there is room to hover; phones get one list */
function useIsDesktop() {
  const query = "(min-width: 768px)";
  const [yes, setYes] = useState(() => typeof window !== "undefined" && window.matchMedia(query).matches);
  useEffect(() => {
    const mq = window.matchMedia(query);
    const on = () => setYes(mq.matches);
    on();
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  return yes;
}

const railButton =
  "relative flex size-11 shrink-0 items-center justify-center rounded-xl transition-colors outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring";
const idle = "text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground";
const current = "bg-sidebar-primary text-sidebar-primary-foreground";

/** The bar on the rail's edge marks the current module, so it doesn't rely on colour alone */
const ActiveMark = () => <span aria-hidden="true" className="absolute -left-[10px] top-1/2 h-5 w-[3px] -translate-y-1/2 rounded-r bg-gold" />;

function RailLink({ item, active }: { item: NavItem; active: boolean }) {
  const Icon = item.icon;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Link to={item.to} aria-label={item.badge ? `${item.label} (${item.badgeTitle || item.badge})` : item.label} aria-current={active ? "page" : undefined} className={cn(railButton, active ? current : idle)}>
          {active && <ActiveMark />}
          <Icon className="size-5" />
          {item.badge > 0 && (
            <span className="absolute right-0 top-0 flex h-4 min-w-4 items-center justify-center rounded-full bg-gold px-1 text-[9px] font-bold leading-none text-sidebar">{badgeText(item.badge)}</span>
          )}
        </Link>
      </TooltipTrigger>
      <TooltipContent side="right" sideOffset={8}>
        {item.label}
        {item.badge > 0 && item.badgeTitle ? ` · ${item.badgeTitle}` : ""}
      </TooltipContent>
    </Tooltip>
  );
}

/**
 * Slim navy rail: the modules you use most, a "More" button that opens every other module, and
 * Settings / account at the bottom. Which modules are pinned is each person's choice (the pencil).
 */
export function IconRail({ nav, className }: { nav: Nav; className?: string }) {
  const desktop = useIsDesktop();
  const pins = usePinnedNav(nav.groups);
  const [moreOpen, setMoreOpen] = useState(false);

  // Only pinned modules are on the bar; opening an unpinned page (from More) doesn't add an icon
  const unpinnedBadge = pins.unpinned.reduce((n, i) => n + i.badge, 0);
  const shown = pins.pinned;

  // The middle list scrolls on short screens; a fade at the top/bottom shows there is more in that direction
  const listRef = useRef<HTMLDivElement>(null);
  const [fade, setFade] = useState({ top: false, bottom: false });
  const measure = useCallback(() => {
    const el = listRef.current;
    if (!el) return;
    const top = el.scrollTop > 2;
    const bottom = el.scrollTop + el.clientHeight < el.scrollHeight - 2;
    setFade((f) => (f.top === top && f.bottom === bottom ? f : { top, bottom }));
  }, []);
  useEffect(() => {
    measure();
    const el = listRef.current;
    if (!el) return;
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    ro?.observe(el);
    window.addEventListener("resize", measure);
    return () => {
      ro?.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [measure, shown.length]);

  const moreButton = (
    <button type="button" aria-label="More" aria-haspopup="menu" className={cn(railButton, "text-sidebar-foreground")}>
      <span className="flex size-8 items-center justify-center rounded-full bg-sidebar-accent transition-colors hover:bg-sidebar-primary hover:text-sidebar-primary-foreground">
        <MoreHorizontal className="size-[18px]" />
      </span>
      {unpinnedBadge > 0 && <span aria-hidden="true" className="absolute right-1 top-1 size-2.5 rounded-full bg-gold ring-2 ring-sidebar" />}
    </button>
  );

  return (
    <nav aria-label="Modules" className={cn("flex w-[64px] shrink-0 flex-col items-center gap-1 bg-sidebar py-3 text-sidebar-foreground", className)}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Link to="/dashboard" aria-label="Nelgates CRM" className="mb-2 flex size-11 items-center justify-center rounded-xl outline-none transition-colors hover:bg-sidebar-accent/60 focus-visible:ring-2 focus-visible:ring-sidebar-ring">
            <img src={markUrl} alt="Nelgates" className="size-8 select-none object-contain drop-shadow-sm" />
          </Link>
        </TooltipTrigger>
        <TooltipContent side="right" sideOffset={8}>
          Nelgates CRM
        </TooltipContent>
      </Tooltip>

      <div className="relative flex min-h-0 w-full flex-1 flex-col">
        <div ref={listRef} onScroll={measure} className="flex w-full flex-1 flex-col items-center gap-1.5 overflow-y-auto overflow-x-hidden py-1 [scrollbar-width:none]">
          {shown.map(({ item, group }, i) => (
            <React.Fragment key={item.key}>
              {i > 0 && shown[i - 1]!.group.key !== group.key && <div className="my-2 h-px w-7 shrink-0 bg-sidebar-border" aria-hidden="true" />}
              <RailLink item={item} active={nav.isActive(item)} />
            </React.Fragment>
          ))}
          {pins.unpinned.length > 0 && (
            <>
              <div className="my-2 h-px w-7 shrink-0 bg-sidebar-border" aria-hidden="true" />
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="shrink-0">
                    <MoreMenu groups={nav.groups} isActive={nav.isActive} pins={pins} desktop={desktop} trigger={moreButton} onOpenChange={setMoreOpen} />
                  </span>
                </TooltipTrigger>
                {!moreOpen && (
                  <TooltipContent side="right" sideOffset={8}>
                    More{unpinnedBadge > 0 ? " · something is waiting" : ""}
                  </TooltipContent>
                )}
              </Tooltip>
            </>
          )}
        </div>
        <div aria-hidden="true" className={cn("pointer-events-none absolute inset-x-0 top-0 h-6 bg-gradient-to-b from-sidebar to-transparent transition-opacity", fade.top ? "opacity-100" : "opacity-0")} />
        <div aria-hidden="true" className={cn("pointer-events-none absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t from-sidebar to-transparent transition-opacity", fade.bottom ? "opacity-100" : "opacity-0")} />
      </div>

      <div className="mt-2 flex flex-col items-center gap-1.5">
        {nav.admin.map((item) => (
          <RailLink key={item.key} item={item} active={nav.isActive(item)} />
        ))}
        <CustomizeNav groups={nav.groups} pins={pins} />
        <UserMenu side="right" />
      </div>
    </nav>
  );
}
